import { AfterViewInit, Component, computed, inject } from '@angular/core';
import { BaseListComponent } from '../base-list/base-list.component';
import { SidebarConfiguration } from '../../../models/sidebar-configuration';
import { MenuItem, MessageService, SharedModule } from 'primeng/api';
import { Topic } from '../../../models/topic';
import { LandingPageAudience } from '../../../models/commune';
import {
  getLandingPageItems,
  hasSeparateLandingPages,
} from '../../../utility/landing-page.utility';
import { TopicStore } from '../../../stores/topic/topic.state';
import { SidebarService } from '../../../services/menu/sidebar.service';
import {
  ActivatedRoute,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { RoutingService } from '../../../services/routing.service';
import { BadgeModule } from 'primeng/badge';
import { HeaderComponent } from '../../atoms/header/header.component';
import { MaskComponent } from '../mask/mask.component';
import { NgClass, NgIf, NgTemplateOutlet } from '@angular/common';
import { SidebarModule } from 'primeng/sidebar';
import { TableCaptionComponent } from '../../molecules/table-list-caption/table-list-caption.component';
import { TableListActionsComponent } from '../../molecules/table-list-actions/table-list-actions.component';
import { TableModule } from 'primeng/table';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { GlobalsStore } from '../../../stores/globals.state';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { TableRowReorderEvent } from 'primeng/table';
import { TopicHelperService } from '../../../services/topic/topic-helper.service';

@Component({
  selector: 'app-topic-administration',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    BadgeModule,
    HeaderComponent,
    MaskComponent,
    NgIf,
    NgClass,
    RouterOutlet,
    SharedModule,
    SidebarModule,
    TableCaptionComponent,
    TableListActionsComponent,
    TableModule,
    AlertComponent,
    RouterLink,
    ButtonModule,
    TooltipModule,
  ],
  templateUrl: './topic-administration.component.html',
  styleUrl: './topic-administration.component.scss',
})
export class TopicAdministrationComponent
  extends BaseListComponent<Topic>
  implements AfterViewInit
{
  sidebarConfig: SidebarConfiguration = {
    cssClasses: 'w-8',
    icon: 'fas fa-book-open',
    headerText: 'Themen',
  };

  readonly store = inject(TopicStore);
  readonly activeUserStore = inject(ActiveUserStore);
  readonly globalStore = inject(GlobalsStore);
  readonly topicHelper = inject(TopicHelperService);
  override menuItems: MenuItem[] = [];
  override sidebarId = 'topic-administration';

  /**
   * The "Fallback-Thema" is not a stored Topic entity but a setting living on
   * `Commune.atlasConfig.fallbackTopic`. It's shown as a pinned, synthetic row
   * so admins can find and configure it alongside real topics.
   */
  fallbackRow = computed(() => {
    const config = this.globalStore.activeCommune()?.atlasConfig?.fallbackTopic;
    return {
      _id: 'fallback',
      title: config?.title || 'Weitere Bereiche',
      icon: config?.icon || 'layer-group',
      published: config?.visible ?? false,
      spotlight: config?.spotlight ?? false,
    };
  });

  /** Badge grouping the status icons of the topic list. */
  readonly statusBadgeClass =
    'inline-flex align-items-center gap-1 px-2 py-1 border-1 surface-border border-round-2xl text-sm';

  /**
   * Topic ids on each landing page; `separated` tells whether the public and
   * the internal atlas have their own landing page (else both share `internal`).
   */
  landingPages = computed(() => {
    const config = this.globalStore.activeCommune()?.atlasConfig;
    const topicIds = (audience: LandingPageAudience): Set<string> =>
      new Set(
        getLandingPageItems(config, audience)
          .filter(item => item.type === 'Topic')
          .map(item => item.id)
      );
    return {
      separated: hasSeparateLandingPages(config),
      public: topicIds('public'),
      internal: topicIds('internal'),
    };
  });

  constructor(
    protected sidebarService: SidebarService,
    router: Router,
    route: ActivatedRoute,
    messageService: MessageService,
    confirmationDialogService: ConfirmationDialogService,
    routingService: RoutingService
  ) {
    super(
      router,
      route,
      messageService,
      confirmationDialogService,
      routingService
    );
  }

  /**
   * A lifecycle hook that is called after Angular has fully initialized
   * the component's view. This method performs a configuration setup
   * for the sidebar service by passing the specified sidebar configuration.
   *
   * @return {void} No return value.
   */
  ngAfterViewInit(): void {
    this.sidebarService.setConfiguration(this.sidebarConfig);
  }

  /**
   * Persists a drag-and-drop row reorder by re-assigning `sorting` on the affected
   * topics (by their new index) and saving all of them in one bulk update - the
   * `updateMany` endpoint rebuilds each document from scratch, so full topic
   * objects are passed, not partial `{_id, sorting}` ones.
   *
   * `store.sorted()` is a memoized computed - since `items` hasn't changed yet
   * at this point, it still returns the exact same array instance that's bound
   * to the table's `[value]`, which PrimeNG's row-reorder directive has
   * already spliced into the correct dropped order in place before emitting
   * this event. Re-applying `moveItemInArray` here would move the item a
   * second time on top of that, landing it one slot off from where it was
   * actually dropped - so the (already correctly ordered) array is only
   * re-numbered, not moved again.
   *
   * @param {TableRowReorderEvent} event - The PrimeNG row reorder event.
   * @return {void} Does not return a value.
   */
  onRowReorder(event: TableRowReorderEvent): void {
    if (event.dragIndex === undefined || event.dropIndex === undefined) return;

    const reordered = this.store.sorted().map(topic => ({ ...topic }));
    reordered.forEach((topic, index) => {
      topic.sorting = index;
    });

    this.store.updateMultiple({ items: reordered });
  }
}
